/**
 * SR Command Center — Assignment Proposal Engine (STAGED)
 * ------------------------------------------------------------------
 * Pure, framework-agnostic scoring logic for routing a championship /
 * pursuit opportunity to the rep who has PROVEN in that sport + region.
 *
 * Data source: rep_proficiency.json (built 2026-08-07 from 2,830 ClickUp
 * won records). Shape documented in INTEGRATION.md. Read the map at
 * RUNTIME so the scheduled state backfill improves suggestions by
 * re-bundling the JSON alone (no code change).
 *
 * GOVERNANCE (propose-don't-overwrite):
 *   - This module ONLY proposes. It never writes.
 *   - A human with the "Manage Assignments" permission approves a
 *     suggestion or reassigns; that click writes `sr_assigned_rep`.
 *   - Auto-suggestions never overwrite an existing sr_assigned_rep.
 *
 * Write field: sr_assigned_rep  (display-name string; NOT hubspot_owner_id
 *   — only 2/15 reps are HubSpot owners. See INTEGRATION.md.)
 */

// ---- tunable weights ------------------------------------------------
const W = {
  sport: 3,        // proven the exact sport
  conference: 3,   // college: proven the exact conference
  governingBody: 3,// HS: proven the exact state governing body
  state: 2,        // proven the exact state
  levelMatch: 1,   // has volume at this competition level (HS/College/Cheer)
};

// Normalize a sport/label for loose matching ("Track and Field" -> "track & field")
function norm(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\band\b/g, '&')
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}

function lookup(counts, key) {
  if (!counts || !key) return 0;
  const target = norm(key);
  for (const k of Object.keys(counts)) {
    if (norm(k) === target) return counts[k] || 0;
  }
  // loose contains match (e.g. "Track & Field" vs "Men's Track & Field")
  for (const k of Object.keys(counts)) {
    const nk = norm(k);
    if (nk && (nk.includes(target) || target.includes(nk))) return counts[k] || 0;
  }
  return 0;
}

function classifyLevel(opp) {
  const lvl = norm(opp.level);
  if (lvl.includes('cheer') || lvl.includes('allstar') || lvl.includes('all star')) return 'Cheer';
  if (lvl.includes('college') || lvl.includes('university') || lvl.includes('ncaa')) return 'College';
  if (lvl.includes('high school') || lvl === 'hs') return 'HS';
  // infer from other fields
  if (opp.conference) return 'College';
  if (opp.governing_body || opp.governingBody) return 'HS';
  return 'Other';
}

function isCheer(opp) {
  const sp = norm(opp.sport);
  return classifyLevel(opp) === 'Cheer' || sp === 'cheerleading' || sp === 'cheer' || sp === 'dance';
}

// ---- core scoring ---------------------------------------------------
/**
 * scoreOpportunity(opp, map) -> proposal
 * opp: { name, sport, state, level, conference, governing_body }
 * map: parsed rep_proficiency.json
 * returns:
 *   { suggested, suggestedDisplayName, why, confidence,
 *     alternates:[{rep,score,why}], writeField:'sr_assigned_rep',
 *     writeValue, needsBackup?:bool, noMatch?:bool }
 */
export function scoreOpportunity(opp, map) {
  const reps = map && map.reps ? map.reps : {};
  // (The map's `routing_rules_hint` documents the rules encoded below; the hard
  // routes are implemented inline rather than read from it.)
  const level = classifyLevel(opp);
  const sport = norm(opp.sport);

  // ---- hard routing rules (from routing_rules_hint) ----
  if (isCheer(opp)) {
    return finalize('Jennifer Loveday-Donovan', reps,
      'Cheer / all-star routes to Jennifer (234 cheer wins, the proven cheer rep). Flag: single-point dependency — assign a backup.',
      'high', [], { needsBackup: true });
  }
  if (sport === 'ice hockey' || sport === 'hockey') {
    return finalize('Jonathan Harrison', reps,
      'Ice hockey routes to Jonathan Harrison (primary proven hockey rep).', 'high', []);
  }
  if (sport.includes('class ring') || sport.includes('graduation') || sport.includes('diploma')) {
    return finalize('Stephanie Pettit', reps,
      'Class-ring / graduation routes to Stephanie Pettit (sole proven owner of class-ring programs).', 'high', []);
  }

  // ---- scored routing ----
  const scored = [];
  for (const repName of Object.keys(reps)) {
    const r = reps[repName];
    let score = 0;
    const reasons = [];

    const sportWins = lookup(r.sports_won, opp.sport);
    if (sportWins > 0) { score += W.sport + Math.min(sportWins, 10) * 0.1; reasons.push(`${sportWins} ${opp.sport} wins`); }

    if (level === 'College') {
      const conf = lookup(r.college_conferences_won, opp.conference);
      if (conf > 0) { score += W.conference + Math.min(conf, 10) * 0.1; reasons.push(`${conf} in ${opp.conference}`); }
      if ((r.level_split && r.level_split.College) > 0) score += W.levelMatch;
    } else if (level === 'HS') {
      const gov = lookup(r.hs_governing_bodies_won, opp.governing_body || opp.governingBody);
      if (gov > 0) { score += W.governingBody + Math.min(gov, 10) * 0.1; reasons.push(`${gov} in ${opp.governing_body || opp.governingBody}`); }
      const st = lookup(r.states_won, opp.state);
      if (st > 0) { score += W.state + Math.min(st, 10) * 0.1; reasons.push(`${st} in ${opp.state}`); }
      if ((r.level_split && r.level_split.HS) > 0) score += W.levelMatch;
    } else {
      const st = lookup(r.states_won, opp.state);
      if (st > 0) { score += W.state + Math.min(st, 10) * 0.1; reasons.push(`${st} in ${opp.state}`); }
    }

    if (score > 0) scored.push({ rep: repName, score: round(score), why: reasons.join(', ') });
  }

  scored.sort((a, b) => b.score - a.score);

  if (scored.length === 0) {
    return {
      suggested: null, suggestedDisplayName: null,
      why: 'No proven match in the win history — needs manual assignment.',
      confidence: 'none', alternates: [], noMatch: true,
      writeField: 'sr_assigned_rep', writeValue: null,
    };
  }

  const top = scored[0];
  const second = scored[1];
  const margin = second ? top.score - second.score : top.score;
  let confidence = 'low';
  if (top.score >= 5 && margin >= 2) confidence = 'high';
  else if (top.score >= 3) confidence = 'medium';

  return finalize(top.rep, reps, top.why, confidence, scored.slice(1, 3));
}

function finalize(repName, reps, why, confidence, alternates, extra = {}) {
  const r = reps[repName] || {};
  return {
    suggested: repName,
    suggestedDisplayName: r.display_name || repName,
    hubspotOwnerId: r.hubspot_owner_id || null, // usually null (only 2/15 resolve)
    why,
    confidence,
    alternates: (alternates || []).map(a => ({ rep: a.rep, score: a.score, why: a.why })),
    writeField: 'sr_assigned_rep',
    writeValue: r.display_name || repName,
    ...extra,
  };
}

function round(n) { return Math.round(n * 100) / 100; }

/**
 * Build the first worklist: the 59 known no-rep won accounts, each with a
 * proposal. Feed this straight into the CC "Assignments" worklist.
 */
export function proposeForUnassigned(map) {
  const list = (map && map.assignment_signals && map.assignment_signals.unassigned_wins
    && map.assignment_signals.unassigned_wins.accounts) || [];
  return list.map(acc => ({
    account: acc.account,
    url: acc.url,
    sport: acc.sport,
    state: acc.state,
    status: acc.status,
    proposal: scoreOpportunity(
      { name: acc.account, sport: acc.sport, state: acc.state,
        level: /university|college/i.test(acc.account) ? 'College' : undefined },
      map),
  }));
}

export const _internal = { norm, lookup, classifyLevel, isCheer, W };
